import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/shared/components/ui/button";

interface FinancePaginationProps {
  readonly page: number;
  readonly limit: number;
  readonly totalCount: number;
  readonly onPageChange: (page: number) => void;
  /** Prefix for the data-testids, e.g. "ledger" → `ledger-pagination-prev`. */
  readonly testIdPrefix: string;
}

export function FinancePagination({
  page,
  limit,
  totalCount,
  onPageChange,
  testIdPrefix,
}: FinancePaginationProps) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const from = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, totalCount);

  return (
    <div
      data-testid={`${testIdPrefix}-pagination`}
      className="flex items-center justify-between gap-3"
    >
      <p className="font-sans text-sm font-normal text-muted-foreground">
        {t("finance.pagination.summary", { from, to, total: totalCount })}
      </p>
      <div className="flex items-center gap-2">
        <Button
          data-testid={`${testIdPrefix}-pagination-prev`}
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label={t("finance.pagination.previous")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="font-sans text-sm font-medium tabular-nums text-foreground">
          {t("finance.pagination.pageOf", { page, total: totalPages })}
        </span>
        <Button
          data-testid={`${testIdPrefix}-pagination-next`}
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label={t("finance.pagination.next")}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
